// ============================================================
// Assessments Page — Composer, Invite, Results
// ============================================================

const API_BASE = import.meta.env.VITE_API_URL || '/api';

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Link } from 'react-router-dom';
import {
    Plus, Edit2, Trash2, Users, Clock, Settings, Send,
    BarChart3, X, Check, Search, ChevronRight, Copy,
    Eye, AlertTriangle, Shield, Shuffle, Hash, BookOpen,
    Mail, ExternalLink, CheckCircle, ChevronDown, Award, Download
} from 'lucide-react';
import toast from 'react-hot-toast';
import { generateAllCandidatesPDF, generateCandidateResultPDF } from '../utils/pdfGenerator';

export function AssessmentsPage() {
    const queryClient = useQueryClient();
    const [showCreate, setShowCreate] = useState(false);
    const [editingAssessment, setEditingAssessment] = useState<any>(null);
    const [activeAssessment, setActiveAssessment] = useState<any>(null);
    const [activePanel, setActivePanel] = useState<'invite' | 'results' | null>(null);
    const [searchQuery, setSearchQuery] = useState('');

    const { data, isLoading } = useQuery({
        queryKey: ['assessments'],
        queryFn: () => api.get('/assessments'),
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => api.delete(`/assessments/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['assessments'] });
            toast.success('Assessment deleted');
        },
    });

    const assessments = data?.data || [];
    const filtered = assessments.filter((a: any) =>
        !searchQuery || a.title.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="animate-fadeIn">
            <div className="page-header">
                <div>
                    <h1>Assessments</h1>
                    <p className="subtitle">Create, configure, and manage candidate assessments</p>
                </div>
                <button className="btn btn-primary" onClick={() => { setEditingAssessment(null); setShowCreate(true); }}>
                    <Plus size={16} /> New Assessment
                </button>
            </div>

            {/* Search */}
            <div className="card mb-6" style={{ padding: '12px 20px' }}>
                <div style={{ position: 'relative' }}>
                    <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
                    <input className="form-input" placeholder="Search assessments..."
                        value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                        style={{ paddingLeft: 36 }} />
                </div>
            </div>

            {/* Assessment List */}
            {filtered.length === 0 && !isLoading ? (
                <div className="card">
                    <div className="empty-state">
                        <div className="icon">📝</div>
                        <h3>No assessments yet</h3>
                        <p>Create an assessment to start evaluating candidates</p>
                        <button className="btn btn-primary mt-4" onClick={() => { setEditingAssessment(null); setShowCreate(true); }}>
                            <Plus size={16} /> Create Assessment
                        </button>
                    </div>
                </div>
            ) : (
                <div className="card-grid card-grid-2">
                    {filtered.map((a: any) => (
                        <AssessmentCard key={a.id} assessment={a}
                            onEdit={() => { setEditingAssessment(a); setShowCreate(true); }}
                            onDelete={() => { if (confirm('Delete this assessment?')) deleteMutation.mutate(a.id); }}
                            onInvite={() => { setActiveAssessment(a); setActivePanel('invite'); }}
                            onResults={() => { setActiveAssessment(a); setActivePanel('results'); }}
                        />
                    ))}
                </div>
            )}

            {/* Create/Edit Modal */}
            {showCreate && (
                <AssessmentComposer
                    assessment={editingAssessment}
                    onClose={() => { setShowCreate(false); setEditingAssessment(null); }}
                    onSaved={() => {
                        queryClient.invalidateQueries({ queryKey: ['assessments'] });
                        setShowCreate(false);
                        setEditingAssessment(null);
                    }}
                />
            )}

            {/* Invite Panel */}
            {activePanel === 'invite' && activeAssessment && (
                <InviteModal assessment={activeAssessment}
                    onClose={() => { setActivePanel(null); setActiveAssessment(null); }} />
            )}

            {/* Results Panel */}
            {activePanel === 'results' && activeAssessment && (
                <ResultsModal assessment={activeAssessment}
                    onClose={() => { setActivePanel(null); setActiveAssessment(null); }} />
            )}
        </div>
    );
}

// ── Assessment Card ──────────────────────────────────────────

function AssessmentCard({ assessment: a, onEdit, onDelete, onInvite, onResults }: {
    assessment: any; onEdit: () => void; onDelete: () => void; onInvite: () => void; onResults: () => void;
}) {
    const queryClient = useQueryClient();
    const config = a.config || {};
    const questionIds = config.selectionRules?.fixedQuestionIds || [];
    const questionCount = questionIds.length || a.questionCount || 0;

    const toggleStatusMutation = useMutation({
        mutationFn: () => api.put(`/assessments/${a.id}`, { status: a.status === 'active' ? 'draft' : 'active' }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['assessments'] });
            toast.success(`Assessment ${a.status === 'active' ? 'deactivated' : 'activated'}`);
        },
        onError: (err: any) => toast.error(err.message),
    });

    return (
        <div className="card">
            <div className="flex justify-between items-center mb-3">
                <button
                    className={`badge badge-${a.status === 'active' ? 'success' : a.status === 'draft' ? 'warning' : 'info'}`}
                    onClick={() => toggleStatusMutation.mutate()}
                    style={{ cursor: 'pointer', border: 'none', padding: '4px 12px' }}
                    title={`Click to ${a.status === 'active' ? 'deactivate' : 'activate'}`}
                    disabled={toggleStatusMutation.isPending}
                >
                    {toggleStatusMutation.isPending ? '...' : a.status}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={onDelete} style={{ color: '#ef4444', padding: 4 }}>
                    <Trash2 size={14} />
                </button>
            </div>

            <h3 style={{ fontWeight: 700, fontSize: '15px', marginBottom: 8 }}>{a.name || a.title}</h3>
            {(config.description || a.description) && <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: 12 }}>{(config.description || a.description)?.substring(0, 100)}</p>}

            {/* Config badges */}
            <div className="flex gap-2 mb-4" style={{ flexWrap: 'wrap' }}>
                <span className="badge badge-accent"><BookOpen size={12} /> {questionCount} questions</span>
                {config.timeLimitMinutes && (
                    <span className="badge badge-info"><Clock size={12} /> {config.timeLimitMinutes}m</span>
                )}
                {config.shuffleQuestions && (
                    <span className="badge badge-info"><Shuffle size={12} /> Shuffled</span>
                )}
                {config.requireOtp && (
                    <span className="badge badge-warning"><Shield size={12} /> OTP</span>
                )}
            </div>

            <div className="flex gap-2">
                <button className="btn btn-ghost btn-sm" onClick={onEdit}><Edit2 size={14} /> Edit</button>
                <button className="btn btn-secondary btn-sm flex-1" onClick={onInvite}><Send size={14} /> Invite</button>
                <button className="btn btn-primary btn-sm flex-1" onClick={onResults}><BarChart3 size={14} /> Results</button>
            </div>
        </div>
    );
}

// ── Assessment Composer (Step-by-Step Wizard) ────────────────

function AssessmentComposer({ assessment, onClose, onSaved }: {
    assessment: any | null; onClose: () => void; onSaved: () => void;
}) {
    const isEditing = !!assessment;
    const existingConfig = assessment?.config || {};

    const [step, setStep] = useState(1);
    const [title, setTitle] = useState(assessment?.name || assessment?.title || '');
    const [description, setDescription] = useState(existingConfig.description || assessment?.description || '');

    // Step 2: Configure multiple sections
    const [sections, setSections] = useState<any[]>(() => {
        if (existingConfig.sections) {
            return [
                { id: 'aptitude', type: 'aptitude', name: 'Aptitude Test', timeLimitMinutes: 15, questionIds: [], enabled: false },
                { id: 'communication', type: 'speaking', name: 'English Speaking Video Test', timeLimitMinutes: 10, questionIds: [], enabled: false },
                { id: 'technical', type: 'subjective', name: 'Technical Assessment', timeLimitMinutes: 30, questionIds: [], enabled: false }
            ].map(s => {
                const found = existingConfig.sections.find((xs: any) => xs.id === s.id || xs.type === s.type);
                if (found) return { ...s, ...found, enabled: true };
                return s;
            });
        }
        
        // Legacy fallback
        const legacyQIds = existingConfig.selectionRules?.fixedQuestionIds ||
            assessment?.questions?.map((q: any) => q.questionId || q.id) || [];
        
        return [
            { id: 'aptitude', type: 'aptitude', name: 'Aptitude Test', timeLimitMinutes: 15, questionIds: [], enabled: false },
            { id: 'communication', type: 'speaking', name: 'English Speaking Video Test', timeLimitMinutes: 10, questionIds: [], enabled: false },
            { id: 'technical', type: 'subjective', name: 'Technical Assessment', timeLimitMinutes: existingConfig.timeLimitMinutes || 30, questionIds: legacyQIds, enabled: true }
        ];
    });

    const [expandedSectionId, setExpandedSectionId] = useState<string | null>('aptitude');
    const [questionSearch, setQuestionSearch] = useState('');

    // Step 3: config settings
    const [shuffleQuestions, setShuffleQuestions] = useState(existingConfig.shuffleQuestions || false);
    const [maxAttempts, setMaxAttempts] = useState(existingConfig.maxAttempts || 3);
    const [requireOtp, setRequireOtp] = useState(existingConfig.requireOtp || false);
    const [passingScore, setPassingScore] = useState(existingConfig.passingScore || 60);

    const [saving, setSaving] = useState(false);

    // Per-section folder filter state
    const [sectionFolderFilter, setSectionFolderFilter] = useState<Record<string, string>>({});

    // Fetch ALL questions for step 2
    const { data: questionsData } = useQuery({
        queryKey: ['questions-all'],
        queryFn: () => api.get('/questions?limit=500'),
    });
    const allQuestions = questionsData?.data || [];

    // Fetch all categories/folders for folder filter
    const { data: categoriesData } = useQuery({
        queryKey: ['question-categories'],
        queryFn: () => api.get('/questions/categories'),
    });
    const allCategories: any[] = categoriesData?.data || [];

    const handleSubmit = async () => {
        if (!title.trim()) return toast.error('Title is required');
        
        const activeSections = sections.filter(s => s.enabled);
        if (activeSections.length === 0) return toast.error('Enable at least one section');

        const hasQuestions = activeSections.some(s => s.questionIds.length > 0);
        if (!hasQuestions) return toast.error('Add at least one question to the enabled sections');

        const totalTime = activeSections.reduce((acc, s) => acc + (Number(s.timeLimitMinutes) || 0), 0);

        const body = {
            title,
            description,
            questionIds: activeSections.flatMap(s => s.questionIds),
            config: {
                sections: activeSections.map(s => ({
                    id: s.id,
                    type: s.type,
                    name: s.name,
                    timeLimitMinutes: Number(s.timeLimitMinutes) || 0,
                    questionIds: s.questionIds,
                })),
                timeLimitMinutes: totalTime,
                shuffleQuestions,
                maxAttempts,
                requireOtp,
                passingScore,
            },
        };

        setSaving(true);
        try {
            if (isEditing) {
                await api.put(`/assessments/${assessment.id}`, body);
                toast.success('Assessment updated');
            } else {
                await api.post('/assessments', body);
                toast.success('Assessment created!');
            }
            onSaved();
        } catch (err: any) {
            toast.error(err.message || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    const steps = [
        { num: 1, label: 'Details' },
        { num: 2, label: 'Questions' },
        { num: 3, label: 'Settings' },
        { num: 4, label: 'Review' },
    ];

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 760, maxHeight: '90vh', overflow: 'auto' }}>
                <div className="flex justify-between items-center mb-4">
                    <h2>{isEditing ? 'Edit Assessment' : 'Create Assessment'}</h2>
                    <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={18} /></button>
                </div>

                {/* Step Indicator */}
                <div className="flex gap-2 mb-6">
                    {steps.map(s => (
                        <div key={s.num} className="flex items-center gap-2 flex-1">
                            <div style={{
                                width: 28, height: 28, borderRadius: '50%', display: 'flex',
                                alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700,
                                background: step >= s.num ? '#6366f1' : 'var(--color-bg-tertiary)',
                                color: step >= s.num ? 'white' : '#6b7280',
                                transition: 'all 0.2s ease',
                             }}>
                                {step > s.num ? <Check size={14} /> : s.num}
                            </div>
                            <span style={{ fontSize: '12px', color: step >= s.num ? 'var(--color-text-primary)' : '#6b7280', fontWeight: 500 }}>
                                {s.label}
                            </span>
                            {s.num < 4 && <div style={{ flex: 1, height: 2, background: step > s.num ? '#6366f1' : 'var(--color-border)', borderRadius: 1 }} />}
                        </div>
                    ))}
                </div>

                {/* Step 1: Details */}
                {step === 1 && (
                    <div>
                        <div className="form-group">
                            <label className="form-label">Assessment Title *</label>
                            <input className="form-input" value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="e.g. Frontend Developer Technical Assessment" />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Description</label>
                            <textarea className="form-textarea" value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Briefly describe what this assessment covers..."
                                rows={3} />
                        </div>
                    </div>
                )}

                {/* Step 2: Configure Sections */}
                {step === 2 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {sections.map((sec, idx) => {
                            const isExpanded = expandedSectionId === sec.id;
                            const activeFolderFilter = sectionFolderFilter[sec.id] || '';

                            // Filter by question type first
                            const typeFiltered = allQuestions.filter((q: any) => {
                                if (sec.type === 'aptitude') return q.type === 'mcq';
                                if (sec.type === 'speaking') return q.type === 'speaking';
                                return q.type !== 'speaking';
                            });

                            // Then filter by selected folder
                            const folderFiltered = activeFolderFilter === '__unassigned'
                                ? typeFiltered.filter((q: any) => !q.categoryId)
                                : activeFolderFilter
                                    ? typeFiltered.filter((q: any) => q.categoryId === activeFolderFilter)
                                    : typeFiltered;

                            // Then filter by search text
                            const filteredSecQuestions = folderFiltered.filter((q: any) =>
                                !questionSearch || (q.currentVersion?.prompt || '').toLowerCase().includes(questionSearch.toLowerCase())
                            );

                            // Questions in current folder filter (for "select all")
                            const folderQIds = filteredSecQuestions.map((q: any) => q.id);
                            const allFolderSelected = folderQIds.length > 0 && folderQIds.every((id: string) => sec.questionIds.includes(id));

                            return (
                                <div key={sec.id} style={{
                                    border: '1.5px solid var(--border-light)',
                                    borderRadius: 16,
                                    background: 'var(--bg-surface)',
                                    padding: '16px 20px',
                                    transition: 'box-shadow 0.2s ease',
                                    boxShadow: 'var(--shadow-card)',
                                }}>
                                    {/* Header Row */}
                                    <div className="flex justify-between items-center">
                                        <label className="flex items-center gap-3 cursor-pointer">
                                            <input type="checkbox" checked={sec.enabled}
                                                onChange={(e) => {
                                                    const updated = [...sections];
                                                    updated[idx].enabled = e.target.checked;
                                                    if (e.target.checked) setExpandedSectionId(sec.id);
                                                    setSections(updated);
                                                }} />
                                            <div>
                                                <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--text-primary)' }}>{sec.name}</div>
                                                <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                                                    {sec.type === 'aptitude' ? 'Mathematical & logical reasoning (MCQ)' :
                                                     sec.type === 'speaking' ? 'Live Webcam English Speaking' :
                                                     'Subjective & objective core skills'}
                                                </div>
                                            </div>
                                        </label>

                                        {sec.enabled && (
                                            <button className="btn btn-secondary btn-sm" onClick={() => setExpandedSectionId(isExpanded ? null : sec.id)}>
                                                <ChevronDown size={14} style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                                                {isExpanded ? 'Collapse' : `${sec.questionIds.length} selected`}
                                            </button>
                                        )}
                                    </div>

                                    {/* Expanded Panel */}
                                    {sec.enabled && isExpanded && (
                                        <div style={{ marginTop: 16, borderTop: '1px solid var(--border-light)', paddingTop: 16 }}>
                                            {/* Name + Time Limit */}
                                            <div className="flex gap-4 mb-4" style={{ flexWrap: 'wrap' }}>
                                                <div className="form-group" style={{ flex: 1, minWidth: 200, marginBottom: 0 }}>
                                                    <label className="form-label" style={{ fontSize: '12px' }}>Custom Test Name</label>
                                                    <input className="form-input" value={sec.name}
                                                        onChange={(e) => {
                                                            const updated = [...sections];
                                                            updated[idx].name = e.target.value;
                                                            setSections(updated);
                                                        }} />
                                                </div>
                                                <div className="form-group" style={{ width: 130, marginBottom: 0 }}>
                                                    <label className="form-label" style={{ fontSize: '12px' }}>Time Limit (min)</label>
                                                    <input type="number" className="form-input" value={sec.timeLimitMinutes} min={1}
                                                        onChange={(e) => {
                                                            const updated = [...sections];
                                                            updated[idx].timeLimitMinutes = Number(e.target.value) || 0;
                                                            setSections(updated);
                                                        }} />
                                                </div>
                                            </div>

                                            {/* Folder Filter + Search + Select All */}
                                            <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                                {/* Folder dropdown */}
                                                <div style={{ flex: 1, minWidth: 160 }}>
                                                    <label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Filter by Folder</label>
                                                    <select
                                                        className="form-select"
                                                        style={{ fontSize: '12px', padding: '7px 10px' }}
                                                        value={activeFolderFilter}
                                                        onChange={(e) => setSectionFolderFilter(prev => ({ ...prev, [sec.id]: e.target.value }))}
                                                    >
                                                        <option value="">All Folders</option>
                                                        <option value="__unassigned">General (No Folder)</option>
                                                        {allCategories.map((cat: any) => (
                                                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                                                        ))}
                                                    </select>
                                                </div>

                                                {/* Search */}
                                                <div style={{ flex: 2, minWidth: 200 }}>
                                                    <label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Search</label>
                                                    <div style={{ position: 'relative' }}>
                                                        <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                                                        <input className="form-input" placeholder="Search questions..."
                                                            value={questionSearch} onChange={(e) => setQuestionSearch(e.target.value)}
                                                            style={{ paddingLeft: 28, fontSize: '12px', padding: '7px 10px 7px 28px' }} />
                                                    </div>
                                                </div>

                                                {/* Select All from current filter */}
                                                {folderQIds.length > 0 && (
                                                    <button
                                                        className="btn btn-sm"
                                                        style={{
                                                            background: allFolderSelected ? 'var(--accent-light)' : 'var(--bg-muted)',
                                                            color: allFolderSelected ? 'var(--accent)' : 'var(--text-muted)',
                                                            border: `1px solid ${allFolderSelected ? 'var(--border-accent)' : 'var(--border)'}`,
                                                            fontSize: '11px', whiteSpace: 'nowrap',
                                                        }}
                                                        onClick={() => {
                                                            const updated = [...sections];
                                                            if (allFolderSelected) {
                                                                // Deselect all in current filter
                                                                updated[idx].questionIds = updated[idx].questionIds.filter((id: string) => !folderQIds.includes(id));
                                                            } else {
                                                                // Add all in current filter
                                                                const merged = Array.from(new Set([...updated[idx].questionIds, ...folderQIds]));
                                                                updated[idx].questionIds = merged;
                                                            }
                                                            setSections(updated);
                                                        }}
                                                    >
                                                        {allFolderSelected ? '✕ Deselect All' : '✓ Select All'} ({folderQIds.length})
                                                    </button>
                                                )}
                                            </div>

                                            {/* Questions scroll list */}
                                            <div style={{ maxHeight: 240, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 5, border: '1px solid var(--border-light)', borderRadius: 10, padding: 6 }}>
                                                {filteredSecQuestions.length === 0 ? (
                                                    <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)', fontSize: '12px' }}>
                                                        {activeFolderFilter ? 'No questions in this folder for this section type.' : 'No matching questions found.'}
                                                    </div>
                                                ) : (
                                                    filteredSecQuestions.map((q: any) => {
                                                        const isSelected = sec.questionIds.includes(q.id);
                                                        const folder = allCategories.find((c: any) => c.id === q.categoryId);
                                                        return (
                                                            <div key={q.id} onClick={() => {
                                                                const updated = [...sections];
                                                                const qIds = updated[idx].questionIds;
                                                                if (qIds.includes(q.id)) {
                                                                    updated[idx].questionIds = qIds.filter((id: string) => id !== q.id);
                                                                } else {
                                                                    updated[idx].questionIds = [...qIds, q.id];
                                                                }
                                                                setSections(updated);
                                                            }}
                                                            style={{
                                                                padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
                                                                background: isSelected ? 'var(--purple-50)' : 'transparent',
                                                                border: `1px solid ${isSelected ? 'var(--purple-200)' : 'transparent'}`,
                                                                transition: 'all 0.12s ease',
                                                                fontSize: '12px',
                                                            }}>
                                                                <div className="flex items-center gap-2">
                                                                    <div style={{
                                                                        width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                                                                        background: isSelected ? 'var(--accent)' : 'transparent',
                                                                        border: `2px solid ${isSelected ? 'var(--accent)' : 'var(--border-strong)'}`,
                                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                    }}>
                                                                        {isSelected && <Check size={11} color="white" />}
                                                                    </div>
                                                                    <div style={{ flex: 1, overflow: 'hidden' }}>
                                                                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-primary)' }}>
                                                                            {q.currentVersion?.prompt || 'No prompt'}
                                                                        </div>
                                                                        {folder && (
                                                                            <div style={{ fontSize: '10px', color: 'var(--accent)', marginTop: 2 }}>📁 {folder.name}</div>
                                                                        )}
                                                                    </div>
                                                                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                                                                        <span className="badge badge-secondary" style={{ fontSize: '9px' }}>{q.type}</span>
                                                                        <span className="badge badge-secondary" style={{ fontSize: '9px' }}>{q.difficulty}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })
                                                )}
                                            </div>

                                            {/* Selection summary */}
                                            <div style={{ marginTop: 10, fontSize: '12px', color: 'var(--text-muted)', textAlign: 'right' }}>
                                                <strong style={{ color: 'var(--accent)' }}>{sec.questionIds.length}</strong> question{sec.questionIds.length !== 1 ? 's' : ''} selected for this section
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Step 3: Settings */}
                {step === 3 && (
                    <div>
                        <div className="form-group">
                            <label className="form-label">Passing Score (%)</label>
                            <div className="flex items-center gap-3">
                                <input type="number" className="form-input" value={passingScore}
                                    onChange={(e) => setPassingScore(Number(e.target.value))}
                                    min={0} max={100} style={{ width: 100 }} />
                                <span style={{ fontSize: '13px', color: '#6b7280' }}>percent</span>
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Max Attempts</label>
                            <div className="flex items-center gap-3">
                                <input type="number" className="form-input" value={maxAttempts}
                                    onChange={(e) => setMaxAttempts(Number(e.target.value))}
                                    min={1} style={{ width: 100 }} />
                                <span style={{ fontSize: '13px', color: '#6b7280' }}>attempts allowed</span>
                            </div>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
                            <label style={{
                                display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                                background: 'var(--color-bg-tertiary)', borderRadius: 10, cursor: 'pointer',
                            }}>
                                <input type="checkbox" checked={shuffleQuestions}
                                    onChange={(e) => setShuffleQuestions(e.target.checked)} />
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: '13px' }}>
                                        <Shuffle size={14} style={{ display: 'inline', marginRight: 6 }} />
                                        Shuffle Questions
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Randomize question order within sections for each candidate</div>
                                </div>
                            </label>

                            <label style={{
                                display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                                background: 'var(--color-bg-tertiary)', borderRadius: 10, cursor: 'pointer',
                            }}>
                                <input type="checkbox" checked={requireOtp}
                                    onChange={(e) => setRequireOtp(e.target.checked)} />
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: '13px' }}>
                                        <Shield size={14} style={{ display: 'inline', marginRight: 6 }} />
                                        Require OTP
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Candidates must verify their email via OTP before starting</div>
                                </div>
                            </label>
                        </div>
                    </div>
                )}

                {/* Step 4: Review */}
                {step === 4 && (
                    <div>
                        <div style={{
                            padding: 20, background: 'var(--color-bg-tertiary)', borderRadius: 12, marginBottom: 16,
                        }}>
                            <h3 style={{ fontWeight: 700, marginBottom: 12 }}>{title}</h3>
                            {description && <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: 16 }}>{description}</p>}

                            <div className="flex gap-3 mb-4" style={{ flexWrap: 'wrap' }}>
                                <span className="badge badge-accent"><BookOpen size={12} /> {sections.filter(s => s.enabled).flatMap(s => s.questionIds).length} questions</span>
                                <span className="badge badge-info"><Clock size={12} /> {sections.filter(s => s.enabled).reduce((acc, s) => acc + (s.timeLimitMinutes || 0), 0)}m total</span>
                                <span className="badge badge-info"><Hash size={12} /> {maxAttempts} attempt{maxAttempts > 1 ? 's' : ''}</span>
                                <span className="badge badge-info"><Award size={12} /> {passingScore}% to pass</span>
                                {shuffleQuestions && <span className="badge badge-warning"><Shuffle size={12} /> Shuffled</span>}
                                {requireOtp && <span className="badge badge-warning"><Shield size={12} /> OTP</span>}
                            </div>

                            <h4 style={{ fontWeight: 600, fontSize: '13px', marginBottom: 8, color: '#4b5563' }}>Assessment Structure:</h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                {sections.filter(s => s.enabled).map((sec, idx) => (
                                    <div key={sec.id} style={{ padding: 12, background: 'var(--color-bg-card)', borderRadius: 8, border: '1px solid var(--color-border)' }}>
                                        <div className="flex justify-between items-center mb-2">
                                            <span style={{ fontWeight: 700, fontSize: '13px' }}>Test {idx + 1}: {sec.name} ({sec.type})</span>
                                            <span className="badge badge-info" style={{ fontSize: '10px' }}>⏱ {sec.timeLimitMinutes}m limit</span>
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                            {sec.questionIds.map((qId: string, qi: number) => {
                                                const q = allQuestions.find((qx: any) => qx.id === qId);
                                                return (
                                                    <div key={qId} style={{ fontSize: '12px', color: 'var(--color-text-secondary)', paddingLeft: 8 }}>
                                                        {qi + 1}. {q ? (q.currentVersion?.prompt || '').substring(0, 100) : qId}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Navigation */}
                <div className="flex justify-between mt-6">
                    <button className="btn btn-secondary" onClick={step === 1 ? onClose : () => setStep(step - 1)}>
                        {step === 1 ? 'Cancel' : '← Back'}
                    </button>
                    {step < 4 ? (
                        <button className="btn btn-primary" onClick={() => {
                            if (step === 1 && !title.trim()) return toast.error('Title is required');
                            if (step === 2) {
                                const activeSections = sections.filter(s => s.enabled);
                                if (activeSections.length === 0) return toast.error('Enable at least one section');
                                const hasQuestions = activeSections.some(s => s.questionIds.length > 0);
                                if (!hasQuestions) return toast.error('Add at least one question to enabled sections');
                            }
                            setStep(step + 1);
                        }}>
                            Next →
                        </button>
                    ) : (
                        <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
                            {saving ? 'Saving...' : isEditing ? 'Update Assessment' : 'Create Assessment'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

// ── Invite Modal — Link-First Approach ────────────────────────

function InviteModal({ assessment, onClose }: { assessment: any; onClose: () => void }) {
    const queryClient = useQueryClient();
    const [generatingLink, setGeneratingLink] = useState(false);
    const [generatedLink, setGeneratedLink] = useState('');
    const [showEmailForm, setShowEmailForm] = useState(false);
    const [emails, setEmails] = useState('');
    const [sending, setSending] = useState(false);

    // Fetch existing invites
    const { data: invitesData } = useQuery({
        queryKey: ['assessment-invites', assessment.id],
        queryFn: () => api.get(`/assessments/${assessment.id}/invites`),
    });
    const existingInvites = invitesData?.data || [];

    const handleGenerateLink = async () => {
        setGeneratingLink(true);
        try {
            const result = await api.post(`/assessments/${assessment.id}/invite`, {});
            const invites = result.data || [];
            if (invites.length > 0) {
                const link = invites[0].link || `${window.location.origin}/assessment/${invites[0].inviteToken || invites[0].token}`;
                setGeneratedLink(link);
                navigator.clipboard.writeText(link);
                toast.success('Invite link generated & copied!');
            }
            queryClient.invalidateQueries({ queryKey: ['assessment-invites', assessment.id] });
        } catch (err: any) {
            toast.error(err.message || 'Failed to generate link');
        } finally {
            setGeneratingLink(false);
        }
    };

    const handleSendByEmail = async () => {
        const emailList = emails.split(/[\n,;]/).map(e => e.trim()).filter(e => e);
        if (emailList.length === 0) return toast.error('Enter at least one email');

        setSending(true);
        try {
            await api.post(`/assessments/${assessment.id}/invite`, { emails: emailList });
            queryClient.invalidateQueries({ queryKey: ['assessment-invites', assessment.id] });
            setEmails('');
            toast.success(`${emailList.length} invite(s) created!`);
        } catch (err: any) {
            toast.error(err.message || 'Failed to create invites');
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 600, maxHeight: '80vh', overflow: 'auto' }}>
                <div className="flex justify-between items-center mb-4">
                    <h2>Invite Candidates — {assessment.name || assessment.title}</h2>
                    <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={18} /></button>
                </div>

                {/* Primary: Generate Link */}
                <div style={{
                    padding: 20, background: 'rgba(99, 102, 241, 0.05)',
                    borderRadius: 12, border: '1px solid rgba(99, 102, 241, 0.2)', marginBottom: 16,
                }}>
                    <h3 style={{ fontWeight: 700, fontSize: '14px', marginBottom: 8 }}>📎 Shareable Invite Link</h3>
                    <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: 12 }}>
                        Generate a unique link that anyone can use to take this assessment.
                    </p>

                    {generatedLink ? (
                        <div>
                            <div style={{
                                display: 'flex', gap: 8, alignItems: 'center',
                                padding: '10px 14px', background: 'var(--color-bg-tertiary)',
                                borderRadius: 8, marginBottom: 8, wordBreak: 'break-all',
                            }}>
                                <ExternalLink size={16} color="#6366f1" style={{ flexShrink: 0 }} />
                                <span style={{ fontSize: '12px', flex: 1, fontFamily: 'monospace', color: '#6366f1' }}>
                                    {generatedLink}
                                </span>
                                <button className="btn btn-primary btn-sm" onClick={() => {
                                    navigator.clipboard.writeText(generatedLink);
                                    toast.success('Copied!');
                                }} style={{ flexShrink: 0 }}>
                                    <Copy size={14} /> Copy
                                </button>
                            </div>
                            <button className="btn btn-ghost btn-sm" onClick={handleGenerateLink} disabled={generatingLink}>
                                Generate Another Link
                            </button>
                        </div>
                    ) : (
                        <button className="btn btn-primary w-full" onClick={handleGenerateLink} disabled={generatingLink}>
                            <ExternalLink size={16} /> {generatingLink ? 'Generating...' : 'Generate Invite Link'}
                        </button>
                    )}
                </div>

                {/* Secondary: Email-based invites */}
                <div style={{ marginBottom: 16 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setShowEmailForm(!showEmailForm)}
                        style={{ fontSize: '13px', color: '#6b7280' }}>
                        <Mail size={14} /> {showEmailForm ? 'Hide' : 'Or invite by email...'}
                        <ChevronDown size={14} style={{ transform: showEmailForm ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                    </button>
                    {showEmailForm && (
                        <div style={{ marginTop: 12 }}>
                            <div className="form-group">
                                <label className="form-label">Candidate Emails</label>
                                <textarea className="form-textarea" value={emails}
                                    onChange={(e) => setEmails(e.target.value)}
                                    placeholder="Enter emails, separated by commas or new lines"
                                    rows={3} />
                            </div>
                            <button className="btn btn-secondary w-full" onClick={handleSendByEmail} disabled={sending}>
                                <Send size={16} /> {sending ? 'Creating...' : 'Create Email Invites'}
                            </button>
                        </div>
                    )}
                </div>

                {/* Existing Invites */}
                {existingInvites.length > 0 && (
                    <div>
                        <h3 style={{ fontWeight: 700, fontSize: '14px', marginBottom: 12 }}>
                            Invite Links ({existingInvites.length})
                        </h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {existingInvites.map((inv: any) => (
                                <div key={inv.id} className="flex items-center gap-3"
                                    style={{
                                        padding: '10px 14px', background: 'var(--color-bg-tertiary)',
                                        borderRadius: 8, fontSize: '13px',
                                    }}>
                                    <ExternalLink size={14} color="#6b7280" />
                                    <span style={{ flex: 1, fontWeight: 500, fontSize: '12px' }}>
                                        {inv.email === 'generic@invite.link' ? 'Shareable Link' : inv.email}
                                    </span>
                                    <span className={`badge badge-${inv.status === 'completed' ? 'success' : inv.status === 'started' ? 'warning' : 'info'}`}
                                        style={{ fontSize: '10px' }}>
                                        {inv.status}
                                    </span>
                                    <button className="btn btn-ghost btn-sm" onClick={() => {
                                        const link = inv.link || `${window.location.origin}/assessment/${inv.inviteToken || inv.token}`;
                                        navigator.clipboard.writeText(link);
                                        toast.success('Link copied!');
                                    }} style={{ padding: 4 }}>
                                        <Copy size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Results Modal ────────────────────────────────────────────

function ResultsModal({ assessment, onClose }: { assessment: any; onClose: () => void }) {
    const { data: resultsData, isLoading } = useQuery({
        queryKey: ['assessment-results', assessment.id],
        queryFn: () => api.get(`/assessments/${assessment.id}/results`),
    });

    const results = resultsData?.data || [];
    const [expandedId, setExpandedId] = useState<string | null>(null);

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 800, maxHeight: '85vh', overflow: 'auto' }}>
                <div className="flex justify-between items-center mb-4">
                    <h2>Results — {assessment.name || assessment.title}</h2>
                    <div className="flex items-center gap-2">
                        {results.length > 0 && (
                            <button
                                className="btn btn-primary btn-sm"
                                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '12px' }}
                                onClick={() => {
                                    try {
                                        generateAllCandidatesPDF(results, assessment.name || assessment.title);
                                        toast.success('PDF downloaded');
                                    } catch (err) {
                                        toast.error('Failed to generate PDF');
                                    }
                                }}
                            >
                                <Download size={14} /> Download All (PDF)
                            </button>
                        )}
                        <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={18} /></button>
                    </div>
                </div>

                {isLoading ? (
                    <div style={{ textAlign: 'center', padding: 40 }}><div className="loading-spinner" /></div>
                ) : results.length === 0 ? (
                    <div className="empty-state">
                        <div className="icon">📊</div>
                        <h3>No results yet</h3>
                        <p>Results will appear here as candidates complete the assessment</p>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {results.map((r: any, i: number) => (
                            <div key={r.id} style={{
                                background: 'var(--color-bg-tertiary)', borderRadius: 12,
                                border: '1px solid var(--color-border)', overflow: 'hidden',
                            }}>
                                {/* Summary Row */}
                                <div
                                    style={{ padding: '14px 18px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 16 }}
                                    onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                                >
                                    <span style={{ color: '#6b7280', fontWeight: 600, width: 24 }}>{i + 1}</span>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 600, fontSize: '14px' }}>{r.candidateName || 'Unknown'}</div>
                                        {r.email && <div style={{ fontSize: '11px', color: '#6b7280' }}>{r.email}</div>}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        {r.scorePercent != null ? (
                                            <>
                                                <div style={{
                                                    width: 60, height: 6, background: 'var(--color-bg-primary)',
                                                    borderRadius: 3, overflow: 'hidden',
                                                }}>
                                                    <div style={{
                                                        width: `${r.scorePercent}%`, height: '100%',
                                                        background: r.scorePercent >= (assessment.config?.passingScore || 60) ? '#10b981' : '#ef4444',
                                                        borderRadius: 3,
                                                    }} />
                                                </div>
                                                <span style={{ fontWeight: 700, fontSize: '14px', minWidth: 40 }}>{r.scorePercent.toFixed(0)}%</span>
                                            </>
                                        ) : (
                                            <span style={{ fontSize: '12px', color: '#f59e0b', fontWeight: 600 }}>⏳ Scoring...</span>
                                        )}
                                    </div>
                                    <span className={`badge badge-${r.status === 'completed' ? 'success' : 'warning'}`} style={{ fontSize: '10px' }}>
                                        {r.status}
                                    </span>
                                    <span style={{ fontSize: '11px', color: '#6b7280', minWidth: 80 }}>
                                        {r.completedAt ? new Date(r.completedAt).toLocaleDateString() : '—'}
                                    </span>
                                    {r.candidateId && (
                                        <Link to={`/candidates/${r.candidateId}`} className="btn btn-ghost btn-sm" onClick={(e) => e.stopPropagation()}>
                                            <Eye size={14} />
                                        </Link>
                                    )}
                                    <button
                                        className="btn btn-ghost btn-sm"
                                        title="Download candidate PDF"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            try {
                                                generateCandidateResultPDF({
                                                    name: r.candidateName, email: r.email,
                                                    assessmentResults: [{ ...r, assessmentTitle: assessment.name || assessment.title }],
                                                    attempts: [{ ...r, assessmentTitle: assessment.name || assessment.title }],
                                                });
                                                toast.success('PDF downloaded');
                                            } catch (err) { toast.error('Failed to generate PDF'); }
                                        }}
                                    >
                                        <Download size={14} />
                                    </button>
                                    <ChevronDown size={16} style={{
                                        color: '#6b7280', transition: 'transform 0.2s',
                                        transform: expandedId === r.id ? 'rotate(180deg)' : 'rotate(0)',
                                    }} />
                                </div>

                                {/* Expanded Detail */}
                                {expandedId === r.id && (
                                    <div style={{ borderTop: '1px solid var(--color-border)', padding: '16px 18px' }}>
                                        {/* Score Breakdown */}
                                        {r.breakdown && (
                                            <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
                                                {r.breakdown.mcqTotal > 0 && (
                                                    <div style={{ background: 'var(--color-bg-primary)', borderRadius: 8, padding: '10px 14px', fontSize: '13px' }}>
                                                        <div style={{ color: '#6b7280', marginBottom: 2 }}>MCQ</div>
                                                        <div style={{ fontWeight: 700 }}>
                                                            {r.breakdown.mcqCorrect}/{r.breakdown.mcqTotal} correct
                                                            <span style={{ color: '#6b7280', fontWeight: 400, marginLeft: 6 }}>
                                                                ({r.mcqScore?.toFixed(0) ?? '0'}%)
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}
                                                {r.breakdown.subjectiveTotal > 0 && (
                                                    <div style={{ background: 'var(--color-bg-primary)', borderRadius: 8, padding: '10px 14px', fontSize: '13px' }}>
                                                        <div style={{ color: '#6b7280', marginBottom: 2 }}>Subjective (AI Scored)</div>
                                                        <div style={{ fontWeight: 700 }}>{r.subjectiveScore?.toFixed(0) || 0}%</div>
                                                    </div>
                                                )}
                                                {r.timeUsedSeconds && (
                                                    <div style={{ background: 'var(--color-bg-primary)', borderRadius: 8, padding: '10px 14px', fontSize: '13px' }}>
                                                        <div style={{ color: '#6b7280', marginBottom: 2 }}>Time Used</div>
                                                        <div style={{ fontWeight: 700 }}>{Math.round(r.timeUsedSeconds / 60)} min</div>
                                                    </div>
                                                )}
                                                {r.breakdown?.subjectivePending && (
                                                    <div style={{ background: 'rgba(245, 158, 11, 0.1)', borderRadius: 8, padding: '10px 14px', fontSize: '13px', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
                                                        <div style={{ color: '#f59e0b', marginBottom: 2 }}>⏳ AI Scoring</div>
                                                        <div style={{ fontWeight: 600, color: '#f59e0b' }}>In Progress...</div>
                                                    </div>
                                                )}
                                                {r.breakdown?.aiError && (
                                                    <div style={{ background: 'rgba(239, 68, 68, 0.1)', borderRadius: 8, padding: '10px 14px', fontSize: '13px', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                                                        <div style={{ color: '#ef4444', marginBottom: 2 }}>⚠️ AI Scoring Failed</div>
                                                        <div style={{ fontWeight: 600, color: '#ef4444', fontSize: '11px' }}>{r.breakdown.aiError.substring(0, 80)}</div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Question-by-Question */}
                                        <div style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                            Question-by-Question Results
                                        </div>
                                        {(!r.responses || r.responses.length === 0) ? (
                                            <div style={{ color: '#6b7280', fontSize: '13px', fontStyle: 'italic' }}>No response details available</div>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                {r.responses.map((resp: any, qi: number) => (
                                                    <div key={resp.id} style={{
                                                        background: 'var(--color-bg-primary)', borderRadius: 8,
                                                        padding: '12px 14px', border: '1px solid var(--color-border)',
                                                    }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                                                            <div style={{ fontSize: '13px', fontWeight: 600 }}>
                                                                Q{qi + 1}. {resp.questionText || `${resp.questionType} question`}
                                                            </div>
                                                            <span className={`badge badge-${resp.questionType === 'mcq' ? 'info' : 'accent'}`} style={{ fontSize: '9px', flexShrink: 0 }}>
                                                                {resp.questionType}
                                                            </span>
                                                        </div>
                                                        <div style={{ fontSize: '13px', color: '#4b5563', marginBottom: resp.subjectiveScore ? 8 : 0 }}>
                                                            {resp.questionType === 'mcq' && resp.correctAnswer ? (() => {
                                                                const selected = resp.answer?.selectedOptionIds || [];
                                                                const correct = resp.correctAnswer.correctOptionIds || [];
                                                                const options = resp.correctAnswer.options || [];
                                                                const isCorrect = JSON.stringify([...selected].sort()) === JSON.stringify([...correct].sort());
                                                                return (
                                                                    <div>
                                                                        {options.map((opt: any) => {
                                                                            const isSelected = selected.includes(opt.id);
                                                                            const isCorrectOpt = correct.includes(opt.id);
                                                                            return (
                                                                                <div key={opt.id} style={{
                                                                                    padding: '4px 8px', margin: '2px 0', borderRadius: 6, fontSize: '12px',
                                                                                    background: isCorrectOpt ? 'rgba(16,185,129,0.1)' : isSelected ? 'rgba(239,68,68,0.1)' : 'transparent',
                                                                                    border: `1px solid ${isCorrectOpt ? '#10b981' : isSelected ? '#ef4444' : 'transparent'}`,
                                                                                    display: 'flex', alignItems: 'center', gap: 6,
                                                                                }}>
                                                                                    {isSelected && isCorrectOpt && <span style={{ color: '#10b981' }}>✓</span>}
                                                                                    {isSelected && !isCorrectOpt && <span style={{ color: '#ef4444' }}>✗</span>}
                                                                                    {!isSelected && isCorrectOpt && <span style={{ color: '#10b981' }}>✓</span>}
                                                                                    <span>{opt.text}</span>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                        <div style={{ marginTop: 4, fontSize: '11px', fontWeight: 600, color: isCorrect ? '#10b981' : '#ef4444' }}>
                                                                            {isCorrect ? '✅ Correct' : '❌ Incorrect'}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })() : resp.questionType === 'speaking' && resp.answer?.videoKey ? (
                                                                <div style={{ marginTop: 8 }}>
                                                                    <div style={{ fontWeight: 600, marginBottom: 8, color: 'var(--color-text-primary)' }}>Candidate Speaking Response:</div>
                                                                    <video
                                                                        src={`${API_BASE}/uploads/${resp.answer.videoKey}`}
                                                                        controls
                                                                        style={{
                                                                            width: '100%',
                                                                            maxWidth: 400,
                                                                            borderRadius: 8,
                                                                            background: '#000',
                                                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                                                        }}
                                                                    />
                                                                </div>
                                                            ) : (
                                                                <>
                                                                    <strong>Answer:</strong>{' '}
                                                                    {typeof resp.answer === 'object'
                                                                        ? (resp.answer?.code || resp.answer?.text || resp.answer?.answer
                                                                            || (resp.answer?.selectedOptionIds?.length ? `Selected: ${resp.answer.selectedOptionIds.length} option(s)` : JSON.stringify(resp.answer)))
                                                                        : String(resp.answer || 'No answer')
                                                                    }
                                                                </>
                                                            )}
                                                        </div>
                                                        {resp.subjectiveScore && (
                                                            <div style={{ background: 'rgba(99, 102, 241, 0.1)', borderRadius: 8, padding: '10px 12px', marginTop: 8 }}>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                                                    <span style={{ fontWeight: 600, fontSize: '12px', color: '#818cf8' }}>AI Score</span>
                                                                    <span style={{ fontWeight: 700, fontSize: '12px' }}>
                                                                        {resp.subjectiveScore.score}/{resp.subjectiveScore.maxScore}
                                                                        {resp.subjectiveScore.confidence != null && (
                                                                            <span style={{ color: '#6b7280', fontWeight: 400, marginLeft: 6 }}>
                                                                                ({(resp.subjectiveScore.confidence * 100).toFixed(0)}% confidence)
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                </div>
                                                                {resp.subjectiveScore.explanation && (
                                                                    <div style={{ fontSize: '12px', color: '#4b5563', lineHeight: 1.5 }}>
                                                                        {resp.subjectiveScore.explanation}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                        {resp.timeSpentSeconds && (
                                                            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: 4 }}>
                                                                ⏱ {Math.round(resp.timeSpentSeconds / 60)}m {resp.timeSpentSeconds % 60}s
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
